import { Divider } from '@mantine/core';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, FormatContent } from 'src/contexts/ContentContext';
import { SellerEventPerformanceMetrics } from 'src/contexts/EventMetricsContext';
import { ContentPlaceholder } from 'src/core/POS/ContentPlaceholder';
import { PosProgressBar } from 'src/core/POS/PosProgressBar/PosProgressBar';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getCurrentLanguage } from 'src/utils/localeUtils';
import { TicketPricePercentiles } from 'src/WebApiController';

import { MarketShareDistributionChart } from './SellerEventPerformanceMetricWidgetsBody';
import * as styles from './SellerEventPerformanceMetricWidgetsBodyMobile.css';

const TicketsListedAndSoldWidget = ({
  performanceMetrics,
  isLoading,
}: {
  performanceMetrics: SellerEventPerformanceMetrics;
  isLoading: boolean;
}) => {
  const progressBarValue = isLoading
    ? 0
    : (performanceMetrics.totalSoldPercentage ?? 0) * 100;
  const progressBarLabel = isLoading
    ? undefined
    : performanceMetrics.formattedTotalSoldPercentage;

  return (
    <Stack
      direction="row"
      gap="l"
      className={styles.mobileMetricsSection}
      alignItems="center"
    >
      <Stack direction="column" gap="m" style={{ flex: 1 }}>
        <Content id={ContentId.TicketsListedAndSold} />

        {isLoading ? (
          <ContentPlaceholder height="4rem" width="40px" />
        ) : (
          <Stack direction="row" gap="m" alignItems="center">
            <span className={styles.mainValue}>
              <FormatContent
                id={FormatContentId.ValueOfValue}
                params={[
                  `${performanceMetrics.formattedTotalSoldQuantity}`,
                  `${performanceMetrics.formattedTotalTicketQuantity}`,
                ]}
              />
            </span>
            <span className={styles.subValue}>{progressBarLabel}</span>
          </Stack>
        )}
      </Stack>
      <Stack direction="column" gap="m" style={{ flex: 1 }}>
        <PosProgressBar value={progressBarValue} />
      </Stack>
    </Stack>
  );
};

const SalesAndLiftWidget = ({
  performanceMetrics,
  isLoading,
}: {
  performanceMetrics: SellerEventPerformanceMetrics;
  isLoading: boolean;
}) => {
  const progressBarValue = isLoading
    ? 0
    : (performanceMetrics.totalGrossMargin ?? 0) * 100;
  const progressBarLabel = isLoading
    ? undefined
    : performanceMetrics.formattedTotalGrossMargin;

  return (
    <Stack
      direction="row"
      gap="l"
      className={styles.mobileMetricsSection}
      alignItems="end"
    >
      <Stack direction="column" gap="m" style={{ flex: 1 }}>
        <Content id={ContentId.SalesAndLift} />

        {isLoading ? (
          <ContentPlaceholder height="4rem" width="40px" />
        ) : (
          <Stack
            direction="row"
            gap="m"
            alignItems="end"
            className={styles.mainValue}
          >
            <span>{performanceMetrics?.formattedTotalNetProceeds},</span>
            <span>{performanceMetrics?.formattedProfitAndLoss}</span>
            <Content id={ContentId.Profit} />
            <span className={styles.subValue}>{progressBarLabel}</span>
          </Stack>
        )}
      </Stack>
      <Stack direction="column" gap="m" style={{ flex: 1 }}>
        <PosProgressBar value={progressBarValue} />
      </Stack>
    </Stack>
  );
};

const TempAverageTicketPriceWidget = ({
  ticketPricePercentiles,
  isLoading,
}: {
  ticketPricePercentiles: Partial<TicketPricePercentiles>;
  isLoading: boolean;
}) => {
  const { loginContext } = useAppContext();

  const formatedPrice = useMemo(() => {
    if (!ticketPricePercentiles?.p50) {
      return undefined;
    }
    return new Intl.NumberFormat(getCurrentLanguage(), {
      style: 'currency',
      notation: 'compact',
      currency: loginContext?.user?.activeAccount?.currencyCode ?? undefined,
    }).format(ticketPricePercentiles.p50);
  }, [
    loginContext?.user?.activeAccount?.currencyCode,
    ticketPricePercentiles.p50,
  ]);

  return (
    <Stack
      direction="row"
      gap="l"
      className={styles.mobileMetricsSection}
      alignItems="center"
    >
      <Stack direction="column" gap="m" style={{ flex: 1 }}>
        <Content id={ContentId.AverageTicketPrice} />

        {isLoading ? (
          <ContentPlaceholder height="4rem" width="40px" />
        ) : (
          <Stack direction="row" gap="m">
            <span className={styles.mainValue}>{formatedPrice}</span>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export const SellerEventPerformanceMetricWidgetsBodyMobile = ({
  performanceMetrics,
  isLoading,
}: {
  performanceMetrics: SellerEventPerformanceMetrics;
  isLoading: boolean;
}) => {
  return (
    <Stack direction="column" gap="l" width="full">
      <Stack direction="column" width="full" className={styles.mobileLayout}>
        <TicketsListedAndSoldWidget
          performanceMetrics={performanceMetrics}
          isLoading={isLoading}
        />
        <Divider color={vars.color.border} />
        <SalesAndLiftWidget
          performanceMetrics={performanceMetrics}
          isLoading={isLoading}
        />
        <Divider color={vars.color.border} />
        <TempAverageTicketPriceWidget
          ticketPricePercentiles={performanceMetrics.ticketPricePercentiles}
          isLoading={isLoading}
        />
      </Stack>

      <MarketShareDistributionChart
        marketShares={performanceMetrics.marketShares}
        isLoading={isLoading}
      />
    </Stack>
  );
};
